import { supabase } from '@/lib/integrations/supabase'
import type { FinancialEntry } from './entriesTypes'

function formatDate(date: string, locale: string): string {
  const [year, month, day] = date.split('-')
  return new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString(locale)
}

async function getPartyPixKeys(entries: FinancialEntry[]): Promise<Record<string, string>> {
  const partyIds = entries
    .filter(entry => entry.entry_type === 'payable')
    .map(entry => entry.party_id)
    .filter((id): id is string => Boolean(id))

  if (partyIds.length === 0) return {}

  const { data: parties } = await supabase
    .from('parties')
    .select('id, pix_key')
    .in('id', [...new Set(partyIds)])

  const pixKeys: Record<string, string> = {}
  for (const party of parties || []) {
    if (party.pix_key) pixKeys[party.id] = party.pix_key
  }

  return pixKeys
}

function formatMoney(amount: number, locale: string, currency: string): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount)
}

export async function generateAccountsText(
  entries: FinancialEntry[],
  locale: string,
  currency: string,
  dateFrom: string,
  dateTo: string,
): Promise<string> {
  try {
    const formattedFrom = formatDate(dateFrom, locale)
    const formattedTo = formatDate(dateTo, locale)
    const partyPixMap = await getPartyPixKeys(entries)

    let text = `CONTAS DE ${formattedFrom} A ${formattedTo}\n`
    text += `Gerado em: ${new Date().toLocaleDateString(locale)} as ${new Date().toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}\n`
    text += '\n' + '='.repeat(100) + '\n\n'

    const receivables = entries.filter(entry => entry.entry_type === 'receivable')
    const payables = entries.filter(entry => entry.entry_type === 'payable')

    if (receivables.length > 0) {
      text += 'CONTAS A RECEBER\n' + '-'.repeat(100) + '\n\n'
      for (const entry of receivables) {
        text += `Descricao: ${entry.description || '-'}\n`
        text += `Pessoa: ${entry.party_name_snapshot || '-'}\n`
        text += `Valor: ${formatMoney(entry.amount, locale, currency)}\n`
        text += `Vencimento: ${formatDate(entry.due_date, locale)}\n`
        text += `Metodo: ${entry.payment_method_snapshot?.toUpperCase() || '-'}\n`
        text += '\n'
      }
      text += '\n'
    }

    if (payables.length > 0) {
      text += 'CONTAS A PAGAR\n' + '-'.repeat(100) + '\n\n'
      for (const entry of payables) {
        const pixKey = entry.party_id && partyPixMap[entry.party_id]
          ? partyPixMap[entry.party_id]
          : entry.party_pix_key_snapshot

        text += `Descricao: ${entry.description || '-'}\n`
        text += `Pessoa: ${entry.party_name_snapshot || '-'}\n`
        text += `Valor: ${formatMoney(entry.amount, locale, currency)}\n`
        text += `Vencimento: ${formatDate(entry.due_date, locale)}\n`
        if (pixKey) text += `Chave PIX: ${pixKey}\n`
        text += '\n'
      }
    }

    return text
  } catch (err) {
    console.error('[generateAccountsText] Error generating text:', err)
    throw err
  }
}

export async function generateAccountsPDF(
  entries: FinancialEntry[],
  locale: string,
  currency: string,
  dateFrom: string,
  dateTo: string,
): Promise<void> {
  try {
    const pdfMakeModule = await import('pdfmake/build/pdfmake') as any
    const pdfMake = pdfMakeModule.default || pdfMakeModule

    const pdfFontsModule = await import('pdfmake/build/vfs_fonts') as any
    const pdfFonts = pdfFontsModule.default || pdfFontsModule

    if (pdfFonts?.pdfMake?.vfs) {
      ;(pdfMake as any).vfs = pdfFonts.pdfMake.vfs
    }

    const formattedFrom = formatDate(dateFrom, locale)
    const formattedTo = formatDate(dateTo, locale)
    const partyPixMap = await getPartyPixKeys(entries)

    const docContent: any[] = [
      {
        text: `Contas de ${formattedFrom} a ${formattedTo}`,
        fontSize: 16,
        bold: true,
        margin: [0, 0, 0, 10],
        alignment: 'center',
      },
      {
        text: `Gerado em: ${new Date().toLocaleDateString(locale)} as ${new Date().toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}`,
        fontSize: 10,
        color: '#666',
        margin: [0, 0, 0, 15],
        alignment: 'center',
      },
    ]

    const tableBody: any[][] = [
      [
        { text: 'Tipo', bold: true, fillColor: '#f0f0f0' },
        { text: 'Descricao', bold: true, fillColor: '#f0f0f0' },
        { text: 'Pessoa', bold: true, fillColor: '#f0f0f0' },
        { text: 'Valor', bold: true, fillColor: '#f0f0f0' },
        { text: 'Vencimento', bold: true, fillColor: '#f0f0f0' },
        { text: 'Chave PIX / Detalhes', bold: true, fillColor: '#f0f0f0' },
      ],
    ]

    for (const entry of entries) {
      const pixOrDetails = entry.entry_type === 'payable'
        ? (entry.party_id && partyPixMap[entry.party_id] ? partyPixMap[entry.party_id] : entry.party_pix_key_snapshot) || ''
        : entry.payment_method_snapshot?.toUpperCase() || ''

      const pixCell = pixOrDetails
        ? [
            { text: 'Copiar ', fontSize: 9, color: '#0066cc' },
            { text: pixOrDetails, fontSize: 9, color: '#0066cc', background: '#f0f8ff' },
          ]
        : { text: '-', fontSize: 9, color: '#999' }

      const valueCell = [
        { text: 'Copiar ', fontSize: 9, color: '#28a745' },
        { text: formatMoney(entry.amount, locale, currency), fontSize: 9, bold: true, color: '#28a745', background: '#f0fff4' },
      ]

      tableBody.push([
        { text: entry.entry_type === 'receivable' ? 'REC' : 'PAG', fontSize: 9 },
        { text: entry.description || '-', fontSize: 9 },
        { text: entry.party_name_snapshot || '-', fontSize: 9 },
        { stack: valueCell },
        { text: formatDate(entry.due_date, locale), fontSize: 9 },
        { stack: Array.isArray(pixCell) ? pixCell : [pixCell] },
      ])
    }

    docContent.push({
      table: {
        headerRows: 1,
        widths: ['5%', '20%', '20%', '13%', '15%', '27%'],
        body: tableBody,
      },
      layout: {
        fillColor: (rowIndex: number) => {
          return rowIndex === 0 ? '#f0f0f0' : rowIndex % 2 === 0 ? '#ffffff' : '#f9f9f9'
        },
        hLineWidth: () => 0.5,
        vLineWidth: () => 0.5,
        hLineColor: '#ddd',
        vLineColor: '#ddd',
      },
      margin: [0, 10],
    })

    docContent.push({
      text: 'Azul = Chave PIX (selecione e copie para a area de transferencia)',
      fontSize: 9,
      color: '#0066cc',
      margin: [0, 15, 0, 3],
      bold: true,
    })
    docContent.push({
      text: 'Verde = Valores (selecione e copie para a area de transferencia)',
      fontSize: 9,
      color: '#28a745',
      margin: [0, 0, 0, 0],
      bold: true,
    })

    const pdf = (pdfMake as any).createPdf({ content: docContent })
    const timestamp = new Date().toISOString().split('T')[0]
    pdf.download(`contas-${timestamp}.pdf`)
  } catch (err) {
    console.error('[generateAccountsPDF] Error generating PDF:', err)
    throw err
  }
}
